import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import { animated, useTransition } from 'react-spring/web.cjs'
import { usePanelStack } from './PanelProvider'

import { AnimationStates } from './panel-animation-states'

import styles from './PanelStack.module.css'
/**
 * Animates panels in and out of a stack. Once animation is done
 * it cleans up the visible stack to only allow one panel in the DOM
 * at a time.
 *
 * This is to mitigate any potential side effects between elements
 * in the panels.
 */
export const PanelStack = ({ children, animationDuration = 250 }) => {
  const {
    stack,
    push,
    isEnterAnimating,
    animationState,
    setAnimationState,
    saveScrollPosition,
    getScrollPosition
  } = usePanelStack()

  const [lockedHeight, setLockedHeight] = useState(null)
  const [containerHeight, setContainerHeight] = useState(null)
  const bodyRef = useRef(null)
  const scrollContainerRef = useRef(null)

  // Keep refs to current values for the scroll handler to avoid stale closures
  const stackRef = useRef(stack)
  const animationStateRef = useRef(animationState)
  // Flag to prevent saving scroll position when we're programmatically resetting it
  const isResettingScroll = useRef(false)

  useEffect(() => {
    stackRef.current = stack
  }, [stack])

  useEffect(() => {
    animationStateRef.current = animationState

    // Start scroll restoration early during POP animation for better UX
    // The blip changing scroll position can be jarring if it happens after the animation
    if (animationState === AnimationStates.POP && stack.length > 0) {
      const scrollParent = findScrollParent(scrollContainerRef.current)
      const currentKey = stack[stack.length - 1].key
      const savedPosition = getScrollPosition(currentKey)

      if (scrollParent && savedPosition > 0) {
        // Use instant scroll to avoid competing with panel animation
        scrollParent.scrollTo({
          top: savedPosition,
          behavior: 'instant'
        })
      }
    }
  }, [animationState, stack, getScrollPosition])

  // Helper to find the actual scrolling parent in the DOM tree
  // This is needed because the scroll container might not be the immediate parent
  const findScrollParent = (element) => {
    let current = element
    while (current && current.parentElement) {
      const style = window.getComputedStyle(current)
      if (style.overflowY === 'scroll' || style.overflowY === 'auto') {
        return current
      }
      current = current.parentElement
    }
    return null
  }

  // Will use children as the first component in the stack
  // this helps decrease consumer's implementation overhead
  const initialChildren = useRef(children)
  useEffect(() => {
    if (React.isValidElement(initialChildren.current)) {
      push(<>{initialChildren.current}</>)
    }
  }, [push])

  useEffect(() => {
    if (
      animationState === AnimationStates.LEAVE_FINISH ||
      animationState === AnimationStates.ENTER_FINISH
    ) {
      setAnimationState(AnimationStates.COMPLETE)
    }
  }, [animationState, setAnimationState, stack])

  // Capture initial height and handle resize
  useEffect(() => {
    const updateHeights = () => {
      if (scrollContainerRef.current && bodyRef.current) {
        const scrollParent = findScrollParent(scrollContainerRef.current)
        const availableHeight = scrollParent
          ? scrollParent.clientHeight
          : window.innerHeight * 0.8 // Fallback: 80% of viewport (conservative estimate for modal)
        setContainerHeight(availableHeight)

        if (stack.length === 1 && !lockedHeight) {
          const naturalHeight = bodyRef.current.scrollHeight
          setLockedHeight(naturalHeight)
        }
      }
    }

    updateHeights()
    window.addEventListener('resize', updateHeights)
    return () => window.removeEventListener('resize', updateHeights)
  }, [stack, lockedHeight])

  // Save scroll positions when scrolling - set up once
  useEffect(() => {
    let scrollParent = null
    let handleScroll = null

    // Wait a bit for DOM to settle
    const timeoutId = setTimeout(() => {
      scrollParent = findScrollParent(scrollContainerRef.current)
      if (!scrollParent) return

      handleScroll = () => {
        // Don't save if we're in the middle of resetting scroll
        if (isResettingScroll.current) return

        // Access current values via refs to avoid stale closures
        const currentStack = stackRef.current
        const currentAnimationState = animationStateRef.current

        if (currentStack.length > 0) {
          // Determine which panel's scroll position to save based on animation state
          // During PUSH/ENTER_START: A new panel is animating in, so save the previous panel's position
          // During other states: Save the current panel's position
          let targetKey
          if (
            currentAnimationState === AnimationStates.PUSH ||
            currentAnimationState === AnimationStates.ENTER_START
          ) {
            // Save for the previous panel (if exists)
            if (currentStack.length > 1) {
              targetKey = currentStack[currentStack.length - 2].key
            }
          } else {
            // For all other states, save for the current panel
            targetKey = currentStack[currentStack.length - 1].key
          }

          if (targetKey) {
            const scrollTop = scrollParent.scrollTop
            saveScrollPosition(targetKey, scrollTop)
          }
        }
      }

      scrollParent.addEventListener('scroll', handleScroll, { passive: true })
    }, 100)

    // Cleanup
    return () => {
      clearTimeout(timeoutId)
      if (scrollParent && handleScroll) {
        scrollParent.removeEventListener('scroll', handleScroll)
      }
    }
  }, [saveScrollPosition]) // Include saveScrollPosition in deps

  const transitionDefs = {
    config: {
      duration: animationDuration
    },
    initial: {
      transform: 'translateX(0px)'
    },
    enter: (item) => async (next, cancel) => {
      // For new panels, reset scroll to top
      // Use the saved positions to determine if this is a new panel
      const savedPosition = getScrollPosition(item.key)
      const isNewPanel = savedPosition === -1
      if (isNewPanel) {
        const scrollParent = findScrollParent(scrollContainerRef.current)
        if (scrollParent) {
          // Set flag to prevent scroll handler from saving this programmatic scroll
          isResettingScroll.current = true
          scrollParent.scrollTop = 0
          // Re-enable scroll saves after next frame to avoid race conditions
          requestAnimationFrame(() => {
            isResettingScroll.current = false
          })
        }
      }

      setAnimationState(AnimationStates.ENTER_START)
      await next({ transform: 'translateX(0px)' })
      setAnimationState(AnimationStates.ENTER_FINISH)
    },
    leave: (item) => async (next, cancel) => {
      setAnimationState(AnimationStates.LEAVE_START)
      await next({
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        transform: 'translateX(100%)'
      })
      setAnimationState(AnimationStates.LEAVE_FINISH)
    },
    from: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      transform: 'translateX(100%)',
      height: '100%'
    }
  }

  const transitions = useTransition(stack, (item) => item.key, transitionDefs)
  // top two panels are visible, all others are removed from the DOM
  const activePanels = transitions.slice(-2)

  // hide bottom panel after animation if neither is in a leaving state
  const hideBottomPanel =
    activePanels.length > 1 &&
    activePanels[0].state === 'update' &&
    activePanels[1].state === 'update'

  return (
    <div ref={scrollContainerRef} className={styles.scrollContainer}>
      <div
        ref={bodyRef}
        className={styles.body}
        style={
          lockedHeight && containerHeight
            ? {
                minHeight: `${Math.min(lockedHeight, containerHeight)}px`
              }
            : {}
        }
      >
        {activePanels.map(({ item: { children }, key, props }, idx) => {
          const isBottomPanel = idx === 0 && activePanels.length > 1
          const hidePanel =
            hideBottomPanel && isBottomPanel && !isEnterAnimating
          const pointerEvents = isBottomPanel ? 'none' : ''
          const zIndex = isBottomPanel ? 1 : 2
          const opacity = hideBottomPanel && isBottomPanel ? 0 : 1

          const isSinglePanel = activePanels.length === 1
          const position = isSinglePanel
            ? 'relative'
            : props.position || 'absolute'

          return (
            !hidePanel && (
              <animated.div
                className={styles.panel}
                aria-hidden={isBottomPanel}
                key={key}
                style={{ ...props, pointerEvents, zIndex, opacity, position }}
              >
                {children}
              </animated.div>
            )
          )
        })}
      </div>
    </div>
  )
}

PanelStack.propTypes = {
  children: PropTypes.node,
  animationDuration: PropTypes.number
}
